import { useAuth } from '../../contexts/AuthContext'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HomeHeader } from './HomeHeader'
import { CompactHeroBanner } from './CompactHeroBanner'
import { BookCarouselSection } from './BookCarouselSection'
import { Search } from '../shared/Search'
// import { MembershipBanner } from './MembershipBanner'
import { LibraryBook, BOOK_GENRES } from '../../types'
import { apiClient } from '../../lib/api'
import { useTranslation } from 'react-i18next'

export function HomePage() {
  const { t } = useTranslation('common')
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [popularBooks, setPopularBooks] = useState<LibraryBook[]>([])
  const [genreBooks, setGenreBooks] = useState<Record<string, LibraryBook[]>>({})
  const [popularBooksLoading, setPopularBooksLoading] = useState(true)
  const [genreLoading, setGenreLoading] = useState<Record<string, boolean>>({})
  const [searchTerm, setSearchTerm] = useState('') // Search term for books
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]) // Array of language codes for multi-select
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]) // Array of CEFR levels for multi-select
  const [topLanguageCodes, setTopLanguageCodes] = useState<string[]>([])

  // Show all genres on home page
  const featuredGenres = BOOK_GENRES

  // Scroll to top on initial page load/refresh
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    let isMounted = true
    
    const loadLibraryBooks = async () => {
      // Load popular books FIRST and wait for it to complete
      // This ensures popular books appear before any genre loads start
      try {
        const popularResult = await apiClient.library.getPopularBooks({ limit: 10 })
        if (isMounted) {
          setPopularBooks(popularResult.books || [])
          setPopularBooksLoading(false)
        }
      } catch (err) {
        console.error('Failed to load popular books:', err)
        if (isMounted) setPopularBooksLoading(false)
      }

      // Only start loading genres AFTER popular books are loaded
      // This prevents overwhelming the network with parallel requests
      // Load genres in smaller batches for better performance
      const batchSize = 3
      for (let i = 0; i < featuredGenres.length; i += batchSize) {
        const batch = featuredGenres.slice(i, i + batchSize)
        
        // Initialize loading state for this batch
        if (isMounted) {
          setGenreLoading(prev => {
            const updated = { ...prev }
            batch.forEach(genre => {
              updated[genre] = true
            })
            return updated
          })
        }

        // Load all genres in this batch in parallel
        const batchPromises = batch.map(async (genre) => {
          try {
            const result = await apiClient.library.getPopularBooks({ genre, limit: 10 })
            if (isMounted) {
              setGenreBooks(prev => ({
                ...prev,
                [genre]: result.books || []
              }))
              setGenreLoading(prev => ({
                ...prev,
                [genre]: false
              }))
            }
          } catch (err) {
            console.error(`Failed to load books for genre ${genre}:`, err)
            if (isMounted) {
              setGenreLoading(prev => ({
                ...prev,
                [genre]: false
              }))
            }
          }
        })

        // Wait for current batch to complete before starting next batch
        await Promise.all(batchPromises)
      }

      // Load top languages AFTER popular books are shown (fire-and-forget, non-critical)
      // This prevents the failing DB query from delaying any critical path
      // UnifiedFilters will use defaults if this fails
      apiClient.library.getTopLanguages()
        .then(data => {
          if (isMounted) {
            setTopLanguageCodes(data.top_languages || [])
          }
        })
        .catch(() => {
          // Silently fail - will use defaults from UnifiedFilters
        })
    }
    
    loadLibraryBooks()
    return () => {
      isMounted = false
    }
  }, [])

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const navigateToLibraryWithFilters = () => {
    // Navigate to library with search term, selected languages, and grades
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (selectedLanguages.length > 0) params.set('language', selectedLanguages.join(','))
    if (selectedGrades.length > 0) params.set('grade', selectedGrades.join(','))
    navigate(`/library?${params.toString()}`)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigateToLibraryWithFilters()
  }

  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4">
        {/* Main Banner */}
        <section className="py-3">
          <CompactHeroBanner />
        </section>

        {/* Membership Banner */}
        {/* <section className="py-3">
          <MembershipBanner />
        </section> */}

        {/* Search and Language Filter */}
        <Search
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSearchSubmit={handleSearch}
          searchPlaceholder={t('library.searchPlaceholderLibrary')}
          selectedLanguage={selectedLanguages}
          selectedGenre=""
          selectedOriginalLanguage=""
          selectedSort=""
          selectedGrades={selectedGrades}
          onLanguageChange={(language) => setSelectedLanguages(Array.isArray(language) ? language : [])}
          onGenreChange={() => {}}
          onOriginalLanguageChange={() => {}}
          onSortChange={() => {}}
          onGradeChange={setSelectedGrades}
          onApplyFilters={navigateToLibraryWithFilters}
          onClearFilters={() => {
            setSearchTerm('')
            setSelectedLanguages([])
            setSelectedGrades([])
          }}
          showGenreFilter={false}
          showOriginalLanguageFilter={false}
          showSortFilter={false}
          showGradeFilter={true}
          useMultiSelect={true}
          topLanguageCodes={topLanguageCodes}
        />

        {/* Most Popular Books */}
        <BookCarouselSection
          title={t('library.mostPopular')}
          books={popularBooks}
          showLanguageFilters={false}
          showSeeAll={true}
          seeAllRoute="/library"
          className="border-b border-border"
          isLoading={popularBooksLoading}
        />

        {/* Genre-based sections */}
        {featuredGenres.map((genre) => (
          <BookCarouselSection
            key={genre}
            title={genre}
            books={genreBooks[genre] || []}
            showLanguageFilters={false}
            showSeeAll={true}
            seeAllRoute="/library"
            className="border-b border-border"
            isLoading={genreLoading[genre] ?? false}
          />
        ))}

        {/* Call to Action */}
        <section className="py-16 text-center bg-gradient-to-b from-background to-muted/30">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('home.didntFind')}
            </h2>
            <h3 className="text-2xl md:text-3xl font-bold text-primary mb-6">
              {t('home.translateAndSimplify')}
            </h3>
            <p className="text-lg text-muted-foreground mb-4">
              {t('home.transformDescription')}
            </p>
            <p className="text-base text-muted-foreground mb-8">
              {t('home.features')}
            </p>
            {!user ? (
              <button 
                className="bg-primary text-primary-foreground px-10 py-4 rounded-lg text-lg font-semibold hover:bg-primary/90 transition-all hover:scale-105 shadow-lg"
                onClick={() => {
                  sessionStorage.setItem('post_login_redirect', '/my-books')
                  navigate('/login')
                }}
              >
                {t('home.getStarted')}
              </button>
            ) : (
              <button 
                className="bg-primary text-primary-foreground px-10 py-4 rounded-lg text-lg font-semibold hover:bg-primary/90 transition-all hover:scale-105 shadow-lg"
                onClick={() => navigate('/my-books')}
              >
                {t('home.goToMyBooks')}
              </button>
            )}
            <p className="text-sm text-muted-foreground mt-4">
              {!user ? t('home.joinThousands') : t('home.uploadAndStart')}
            </p>
          </div>
        </section>


        {/* Content Section - What is SimplaBooka */}
        <section className="seo-content py-12 border-b border-border">
          <div className="max-w-4xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              {t('seo.title')}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              {t('seo.description')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="seo-feature p-4 rounded-lg bg-muted/30">
                <div className="text-3xl mb-3">🎯</div>
                <h3 className="font-semibold text-foreground mb-2">{t('seo.feature1Title')}</h3>
                <p className="text-sm text-muted-foreground">{t('seo.feature1Desc')}</p>
              </div>
              <div className="seo-feature p-4 rounded-lg bg-muted/30">
                <div className="text-3xl mb-3">📚</div>
                <h3 className="font-semibold text-foreground mb-2">{t('seo.feature2Title')}</h3>
                <p className="text-sm text-muted-foreground">{t('seo.feature2Desc')}</p>
              </div>
              <div className="seo-feature p-4 rounded-lg bg-muted/30">
                <div className="text-3xl mb-3">✨</div>
                <h3 className="font-semibold text-foreground mb-2">{t('seo.feature3Title')}</h3>
                <p className="text-sm text-muted-foreground">{t('seo.feature3Desc')}</p>
              </div>
            </div>
            <a 
              href="/how-it-works/" 
              className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {t('seo.learnMore')} →
            </a>
          </div>
        </section>


      </div>
    </div>
  )
}
